/* eslint-disable react/jsx-props-no-spreading */
import React, { type ReactNode } from 'react';
import { ModalConsumer } from 'components/core/modal/Context';
import { IntlProvider } from 'react-intl';

import { locale, messages } from 'helpers/intl-helper';
import { Modal } from './constants';

import AddProjectUsersModal from 'components/projects/users/AddProjectUsersModal';
import AddToReportModal from 'components/reports/AddToReport/AddToReportModal';
import BeforeAfterValuePropsModal from 'components/paywall/BeforeAfterValuePropsModal';
import TaskListAssignValuePropsModal from 'components/paywall/TaskListAssignValuePropsModal';
import CollabChecklistsValuePropsModal from 'components/paywall/CollabChecklistsValuePropsModal';
import CompanyDashboardModal from 'components/dashboards/company/modal/CompanyDashboardModal.tsx';
import ConfirmationModal from 'components/core/ConfirmationModal';
import CreateReport from 'components/reports/CreateReport';
import CreateReportFromTemplate from 'components/reports/CreateReportFromTemplate';
import DeletePhotosModal from 'components/photos/DeletePhotosModal';
import EndCollaborationConfirmModal from 'components/projects/collaboration/components/EndCollaborationConfirmModal';
import GalleryLinksValuePropsModal from 'components/paywall/GalleryLinksValuePropsModal';
import ProjectCollaborationValuePropsModal from 'components/paywall/ProjectCollaborationValuePropsModal';
import RearrangeModal from 'components/reports/Rearrange/RearrangeModal';
import ReferencePhotosModal from 'components/projects/taskList/ReferencePhotosModal.jsx';
import ReportsValuePropsModal from 'components/paywall/ReportsValuePropsModal';
import ShowcaseSetupGuide from 'components/showcase/setupGuide/Index';
import TimelineLinksValuePropsModal from 'components/paywall/TimelineLinksValuePropsModal';
import TodosValuePropsModal from 'components/paywall/TodosValuePropsModal';
import ShowcaseWizard from 'components/showcase/wizard/Root';
import TemplatesModal from 'components/projects/taskList/templates/TemplatesModal';
import TaskListFormModal from 'components/projects/taskList/TaskListFormModal';
import MessageModal from 'components/shared/MessageModal.jsx';
import ProjectUsersModal from 'components/projects/users/ProjectUsersModal.jsx';
import TrashCanModal from 'components/projects/trashCan/TrashCanModal.jsx';
import CancelSampleModal, {
  type CancelSampleModalProps,
} from 'components/billing/v2/trials/CancelSampleModal';
import TrustyHypeModal from 'components/projects/show/customers/modals/TrustyHypeModal';
import TrustyValuePropsModal from 'components/projects/show/customers/modals/TrustyValuePropModal';
import CompanyDocumentsModal from 'components/documents/CompanyDocumentsModal';
import DocumentUploadModal from 'components/documents/DocumentUploadModal';
import UploadCompanyDocumentModal from 'components/templates/documents/UploadCompanyDocumentModal';
import PaymentsValuePropModal from 'components/payments/modals/PaymentsValuePropModal';
import PaymentRequestModal from 'components/payments/modals/PaymentRequestModal';
import ReferralModal from 'components/referrals/ReferralModal';
import PagesVideoModal from 'components/projects/pages/PagesVideoModal';
import RestrictedFeatureModal from 'components/projects/show/RestrictedFeatureModal';
import QuickbooksOnlineModal from 'components/projects/show/integrationActions/QuickbooksOnlineModal';
import WatchDemoModal, {
  type WatchDemoModalProps,
} from 'components/shared/VideoModal/WatchDemoModal';
import StripePaymentMethodModal from 'components/billing/v2/paymentMethods/StripePaymentMethodModal';
import StripePaymentWrapper from 'components/billing/v2/paymentMethods/StripePaymentWrapper';
import UsePageTemplateModal from 'components/projects/pages/templates/UsePageTemplateModal';
import type { ShowcaseWizardRootProps } from 'components/showcase/wizard/Root';
import type { StripePaymentMethodModalProps } from 'components/billing/v2/paymentMethods/StripePaymentMethodModal';
import ShareModal from 'components/shared/ShareModal.jsx';
import SaveAsTemplateModal, {
  type SaveAsTemplateModalProps,
} from 'components/projects/pages/templates/SaveAsTemplateModal';
import type { UsePageTemplateModalProps } from 'components/projects/pages/templates/UsePageTemplateModal';
import PageTemplatesPromoModal from 'components/projects/pages/templates/PageTemplatesPromoModal';
import SignaturesPromoModal from 'components/billing/v2/shared/SignaturesPromoModal';
import {
  CreateReviewProfileModal,
  type CreateReviewProfileModalProps,
} from 'components/projects/show/CreateReviewProfileModal';
import {
  CreatePublicProfileModal,
  type CreatePublicProfileModalProps,
} from 'components/publicProfile/CreatePublicProfileModal/CreatePublicProfileModal';
import {
  EditPublicProfileModal,
  type EditPublicProfileModalProps,
} from 'components/publicProfile/EditPublicProfileModal/EditPublicProfileModal';
import ShowcaseWidgetPromoModal from 'components/showcase/ShowcaseWidgetPromoModal';
import EditReviewProfileNicknameModal, {
  type EditReviewProfileNicknameModalProps,
} from 'components/reviews/EditReviewProfileNicknameModal';
import DeleteReviewProfileModal, {
  type DeleteReviewProfileModalProps,
} from 'components/reviews/DeleteReviewProfileModal';
import ReviewRequestModal from 'components/reviews/reviewRequests/ReviewRequestModal';
import ReviewRequestVideoModal from 'components/reviews/reviewRequests/ReviewRequestVideoModal';
import SelectShowcaseProjectModal from 'components/showcase/wizard/SelectShowcaseProjectModal';
import PaymentRequestModalV2, {
  type PaymentRequestModalV2Props,
} from 'components/payments/modals/PaymentRequestModalV2';
import {
  SetReminderModal,
  type SetReminderModalProps,
} from 'components/activity/reminders/SetReminderModal';
import SampleModal from 'components/samples/SampleModal';
import PageAssetPickerModal from 'components/projects/pages/assetPicker/PageAssetPickerModal';
import ReviewsUpgradeModal from 'components/reviews/ReviewsUpgradeModal';
import MapWizardModal from 'components/portfolio/mapWizardModal/MapWizardModal';
import PagePdfExportModal, {
  type PagePdfExportModalProps,
} from 'components/projects/pages/modals/PagePdfExportModal';
import EmbeddedMapPromoModal from 'components/portfolio/mapWizardModal/EmbeddedMapPromoModal';

const defaultRichTextElementsStrong = (chunks: ReactNode) => (
  <strong>{chunks}</strong>
);
const defaultRichTextElementsP = (chunks: ReactNode) => <p>{chunks}</p>;

const ModalRoot = () => {
  const renderModal = (
    componentName: string,
    props: Record<string, unknown>,
    hideModal: () => void,
  ) => {
    switch (componentName) {
      case Modal.SIGNATURES_PROMO:
        return <SignaturesPromoModal {...props} onRequestClose={hideModal} />;
      case Modal.ADD_PROJECT_USERS:
        return <AddProjectUsersModal {...props} onRequestClose={hideModal} />;
      case Modal.ADD_TO_REPORT:
        return <AddToReportModal {...props} onRequestClose={hideModal} />;
      case Modal.COMPANY_DASHBOARD_MODAL:
        return <CompanyDashboardModal {...props} onRequestClose={hideModal} />;
      case Modal.CONFIRMATION:
        return <ConfirmationModal {...props} onRequestClose={hideModal} />;
      case Modal.CREATE_REPORT:
        // @ts-expect-error
        return <CreateReport {...props} onRequestClose={hideModal} />;
      case Modal.CREATE_REPORT_FROM_TEMPLATE:
        return (
          <CreateReportFromTemplate {...props} onRequestClose={hideModal} />
        );
      case Modal.DELETE_PHOTOS:
        return <DeletePhotosModal {...props} onRequestClose={hideModal} />;
      case Modal.END_COLLABORATION_CONFIRM:
        return (
          <EndCollaborationConfirmModal {...props} onRequestClose={hideModal} />
        );
      case Modal.MESSAGE_MODAL:
        return <MessageModal {...props} onRequestClose={hideModal} />;
      case Modal.PROJECT_USERS:
        return <ProjectUsersModal {...props} onRequestClose={hideModal} />;
      case Modal.PROJECT_TRASH_CAN:
        return <TrashCanModal {...props} onRequestClose={hideModal} />;
      case Modal.REARRANGE_REPORT_ITEMS:
        return <RearrangeModal {...props} />;
      case Modal.SHOWCASE_SETUP_GUIDE:
        return <ShowcaseSetupGuide {...props} onRequestClose={hideModal} />;
      case Modal.UPGRADE_TASK_LIST_ASSIGN_MODAL:
        return (
          <TaskListAssignValuePropsModal
            {...props}
            onRequestClose={hideModal}
          />
        );
      case Modal.UPGRADE_BEFORE_AFTER:
        return (
          <BeforeAfterValuePropsModal {...props} onRequestClose={hideModal} />
        );
      case Modal.UPGRADE_COLLAB_CHECKLISTS:
        return (
          <CollabChecklistsValuePropsModal
            {...props}
            onRequestClose={hideModal}
          />
        );
      case Modal.UPGRADE_GALLERY_LINKS:
        return (
          <GalleryLinksValuePropsModal {...props} onRequestClose={hideModal} />
        );
      case Modal.UPGRADE_PROJECT_COLLABORATION:
        return (
          <ProjectCollaborationValuePropsModal
            {...props}
            onRequestClose={hideModal}
          />
        );
      case Modal.REFERENCE_PHOTOS_MODAL:
        return <ReferencePhotosModal {...props} onRequestClose={hideModal} />;
      case Modal.UPGRADE_REPORTS:
        return <ReportsValuePropsModal {...props} onRequestClose={hideModal} />;
      case Modal.UPGRADE_TIMELINE_LINKS:
        return (
          <TimelineLinksValuePropsModal {...props} onRequestClose={hideModal} />
        );
      case Modal.UPGRADE_TODOS:
        return <TodosValuePropsModal {...props} onRequestClose={hideModal} />;
      case Modal.SHOWCASE_WIZARD:
        return <ShowcaseWizard {...(props as ShowcaseWizardRootProps)} />;
      case Modal.TEMPLATES_MODAL:
        return <TemplatesModal {...props} onRequestClose={hideModal} />;
      case Modal.TASK_LIST_FORM_MODAL:
        return <TaskListFormModal {...props} onRequestClose={hideModal} />;
      case Modal.CANCEL_SAMPLE_MODAL:
        return (
          <CancelSampleModal
            {...(props as CancelSampleModalProps)}
            onRequestClose={hideModal}
          />
        );
      case Modal.TRUSTY_HYPE_MODAL:
        // @ts-expect-error
        return <TrustyHypeModal {...props} onRequestClose={hideModal} />;
      case Modal.TRUSTY_VALUE_PROPS_MODAL:
        return <TrustyValuePropsModal {...props} onRequestClose={hideModal} />;
      case Modal.COMPANY_DOCUMENTS_MODAL:
        return <CompanyDocumentsModal {...props} onRequestClose={hideModal} />;
      case Modal.DOCUMENT_UPLOAD_MODAL:
        return <DocumentUploadModal {...props} onRequestClose={hideModal} />;
      case Modal.UPLOAD_COMPANY_DOCUMENT_MODAL:
        return <UploadCompanyDocumentModal {...props} />;
      case Modal.PAYMENTS_VALUE_PROPS_MODAL:
        return <PaymentsValuePropModal {...props} onRequestClose={hideModal} />;
      case Modal.PAYMENT_REQUEST_MODAL:
        // @ts-expect-error
        return <PaymentRequestModal {...props} onRequestClose={hideModal} />;
      case Modal.PAYMENT_REQUEST_V2_MODAL:
        return (
          <PaymentRequestModalV2
            {...(props as PaymentRequestModalV2Props)}
            onRequestClose={hideModal}
          />
        );
      case Modal.REFERRAL_MODAL:
        return <ReferralModal {...props} onRequestClose={hideModal} />;
      case Modal.PAGES_VIDEO_MODAL:
        return <PagesVideoModal {...props} onRequestClose={hideModal} />;
      case Modal.RESTRICTED_FEATURE_MODAL:
        return <RestrictedFeatureModal {...props} onRequestClose={hideModal} />;
      case Modal.SEND_TO_QUICKBOOKS:
        return <QuickbooksOnlineModal {...props} onRequestClose={hideModal} />;
      case Modal.WATCH_DEMO_MODAL:
        return (
          <WatchDemoModal
            {...(props as WatchDemoModalProps)}
            onRequestClose={hideModal}
          />
        );
      case Modal.STRIPE_PAYMENT_MODAL:
        return (
          <StripePaymentWrapper>
            <StripePaymentMethodModal
              {...(props as StripePaymentMethodModalProps)}
              onRequestClose={hideModal}
            />
          </StripePaymentWrapper>
        );
      case Modal.USE_PAGE_TEMPLATE_MODAL:
        return (
          <UsePageTemplateModal {...(props as UsePageTemplateModalProps)} />
        );
      case Modal.CREATE_REVIEW_PROFILE_MODAL:
        return (
          <CreateReviewProfileModal
            {...(props as CreateReviewProfileModalProps)}
          />
        );
      case Modal.SHARE_MODAL:
        return <ShareModal {...props} />;

      case Modal.SAVE_PAGE_AS_TEMPLATE_MODAL:
        return <SaveAsTemplateModal {...(props as SaveAsTemplateModalProps)} />;
      case Modal.PAGE_TEMPLATE_PAYWALL:
        return (
          <PageTemplatesPromoModal {...props} onRequestClose={hideModal} />
        );
      case Modal.CREATE_PUBLIC_PROFILE:
        return (
          <CreatePublicProfileModal
            {...(props as CreatePublicProfileModalProps)}
          />
        );
      case Modal.EDIT_PUBLIC_PROFILE:
        return (
          <EditPublicProfileModal {...(props as EditPublicProfileModalProps)} />
        );
      case Modal.SHOWCASE_WIDGET_PROMO:
        return <ShowcaseWidgetPromoModal {...props} />;
      case Modal.EDIT_REVIEW_PROFILE_NICKNAME_MODAL:
        return (
          <EditReviewProfileNicknameModal
            {...(props as EditReviewProfileNicknameModalProps)}
          />
        );
      case Modal.DELETE_REVIEW_PROFILE_MODAL:
        return (
          <DeleteReviewProfileModal
            {...(props as DeleteReviewProfileModalProps)}
          />
        );
      case Modal.EXPORT_PAGE_PDF_MODAL:
        return <PagePdfExportModal {...(props as PagePdfExportModalProps)} />;
      case Modal.REVIEW_REQUEST_MODAL:
        return <ReviewRequestModal {...props} />;
      case Modal.REVIEW_REQUEST_VIDEO_MODAL:
        return (
          <ReviewRequestVideoModal {...props} onRequestClose={hideModal} />
        );
      case Modal.SELECT_SHOWCASE_PROJECT:
        return <SelectShowcaseProjectModal {...props} />;
      case Modal.SET_A_REMINDER:
        return (
          <SetReminderModal
            {...(props as SetReminderModalProps)}
            onRequestClose={hideModal}
          />
        );
      case Modal.SAMPLES_UPGRADE_MODAL:
        return <SampleModal {...props} onRequestClose={hideModal} />;
      case Modal.PAGE_ASSET_PICKER_MODAL:
        return <PageAssetPickerModal {...props} />;
      case Modal.REVIEWS_UPGRADE_MODAL:
        return <ReviewsUpgradeModal {...props} />;
      case Modal.MAP_EMBED_WIZARD:
        return <MapWizardModal />;
      case Modal.MAP_EMBED_PROMO:
        return <EmbeddedMapPromoModal />;
      default:
        return null;
    }
  };

  return (
    <IntlProvider
      locale={locale}
      defaultLocale="en"
      key={locale}
      messages={messages}
      textComponent="span"
      defaultRichTextElements={{
        strong: defaultRichTextElementsStrong,
        p: defaultRichTextElementsP,
      }}
    >
      <ModalConsumer>
        {({ componentName, hideModal, props }) => {
          return renderModal(componentName, props, hideModal);
        }}
      </ModalConsumer>
    </IntlProvider>
  );
};

export default ModalRoot;
